/**
 * Platform Detection Utilities
 * 
 * Detects the current platform (web, iOS, Android, React Native)
 * and extracts platform identifiers (origin, bundle ID, package name).
 */

export type PlatformType = 'web' | 'ios' | 'android';

export interface PlatformInfo {
  type: PlatformType;
  identifier: string;
}

/**
 * Detect current platform and extract identifier
 * 
 * @returns Platform type and identifier
 */
export function detectPlatform(): PlatformInfo {
  // Web browser
  if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
    return {
      type: 'web',
      identifier: window.location.origin,
    };
  }

  // React Native
  if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    // For React Native, bundle ID must be passed via config
    // We can't auto-detect it from JavaScript
    throw new Error(
      'React Native detected. Please provide platformIdentifier in config: ' +
      '{ platformType: "ios" | "android", platformIdentifier: "com.your.app" }'
    );
  }

  // Node.js / SSR
  const g = globalThis as unknown as { process?: { versions?: { node?: string } } };
  if (g.process?.versions?.node) {
    throw new Error(
      'TryStyle SDK cannot auto-detect platform in server-side environments. ' +
      'Please use server-side token generation with clientId + clientSecret.'
    );
  }

  throw new Error('Unable to detect platform');
}

/**
 * Validate platform identifier format
 * 
 * @param type - Platform type
 * @param identifier - Platform identifier
 * @throws Error if invalid
 */
export function validatePlatformIdentifier(
  type: PlatformType,
  identifier: string
): void {
  if (!identifier || typeof identifier !== 'string') {
    throw new Error('Platform identifier must be a non-empty string');
  }

  if (type === 'web') {
    try {
      new URL(identifier);
    } catch {
      throw new Error(`Invalid web platform identifier (must be a valid URL): ${identifier}`);
    }
  }

  // For iOS/Android, bundle IDs should follow reverse-domain notation
  // e.g., com.company.app
  if (type === 'ios' || type === 'android') {
    const bundleIdPattern = /^[a-zA-Z0-9.-]+$/;
    if (!bundleIdPattern.test(identifier)) {
      throw new Error(
        `Invalid ${type} platform identifier (must be alphanumeric with dots/hyphens): ${identifier}`
      );
    }
  }
}
